import React, { useState } from 'react';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function isoDate(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// Multi-select calendar. Clients describe ad-hoc pay as dates rather than a count
// ("these three, plus a day each weekend for the rest of the month"), so the dates
// are what gets picked and the count falls out of it — nobody has to do the
// arithmetic in their head and hope they got it right.
//
//   value     array of ISO date strings (YYYY-MM-DD)
//   onChange  (nextDates) => void — always sorted
//   initialMonth  optional Date to open on (defaults to today)
export default function DayPicker({ value = [], onChange, initialMonth }) {
  const start = initialMonth instanceof Date && !Number.isNaN(initialMonth.valueOf())
    ? initialMonth
    : new Date();
  const [year, setYear] = useState(start.getFullYear());
  const [month, setMonth] = useState(start.getMonth());

  const navigate = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };

  const toggle = (dateStr) => {
    const next = value.includes(dateStr)
      ? value.filter(d => d !== dateStr)
      : [...value, dateStr].sort();
    onChange(next);
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOfWeek = new Date(year, month, 1).getDay(); // 0 = Sunday
  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => navigate(-1)} aria-label="Previous month"
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
          <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-200">
          {MONTH_NAMES[month]} {year}
        </span>
        <button type="button" onClick={() => navigate(1)} aria-label="Next month"
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
          <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
          <div key={d} className="text-[11px] font-medium text-gray-400 py-1">{d}</div>
        ))}
        {cells.map((day, idx) => {
          if (day === null) return <div key={`empty-${idx}`} />;
          const dateStr = isoDate(year, month, day);
          const isSelected = value.includes(dateStr);
          const dayOfWeek = new Date(year, month, day).getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          return (
            <button
              key={day}
              type="button"
              aria-pressed={isSelected}
              onClick={() => toggle(dateStr)}
              className={`text-[12px] py-1.5 rounded transition-colors ${
                isSelected
                  ? 'bg-brand-600 text-white font-semibold'
                  : isWeekend
                    ? 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-brand-50 dark:hover:bg-gray-700'
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
